import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Calendar, Clock, ArrowRight, ArrowLeft, CheckCircle, 
  AlertCircle, Home, TreePine, Wrench, Truck, FileText, Car, Building,
  Upload, Phone, FileUp, Check
} from 'lucide-react';
import { services } from '../mock';
import { Header } from './Header';
import { Footer } from './Footer';
import { toast } from 'sonner';

// Guide character component - Same as in PremiumQuoteTunnel
const GuideCharacter = ({ message }) => {
  return (
    <div className="flex items-start gap-3 bg-gradient-to-r from-[#0A2320]/10 to-[#153254]/10 rounded-xl p-3 mb-4 border border-[#0A2320]/20">
      <div className="flex-shrink-0">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-[#0A2320] shadow-md bg-gradient-to-b from-[#f0f4f3] to-[#e8f0ed]">
          <svg width="100%" height="100%" viewBox="0 0 64 64">
            <ellipse cx="32" cy="60" rx="22" ry="12" fill="#0A2320"/>
            <ellipse cx="32" cy="58" rx="18" ry="10" fill="#0A2320"/>
            <rect x="27" y="42" width="10" height="10" fill="#f5dcc3"/>
            <circle cx="32" cy="28" r="18" fill="#f5dcc3"/>
            <ellipse cx="32" cy="18" rx="16" ry="10" fill="#3d3d3d"/>
            <path d="M18 26 Q20 10 32 12 Q44 10 46 26 Q40 18 32 17 Q24 18 18 26" fill="#3d3d3d"/>
            <circle cx="26" cy="28" r="2.5" fill="#3d3d3d"/>
            <circle cx="38" cy="28" r="2.5" fill="#3d3d3d"/>
            <circle cx="26.5" cy="27.5" r="0.8" fill="white"/>
            <circle cx="38.5" cy="27.5" r="0.8" fill="white"/>
            <path d="M22 23 Q26 21 29 23" stroke="#4a4a4a" strokeWidth="1.2" fill="none"/>
            <path d="M35 23 Q38 21 42 23" stroke="#4a4a4a" strokeWidth="1.2" fill="none"/>
            <path d="M26 35 Q32 40 38 35" stroke="#c9a08a" strokeWidth="2" fill="none" strokeLinecap="round"/>
            <circle cx="20" cy="32" r="3" fill="#f5c4b8" opacity="0.5"/>
            <circle cx="44" cy="32" r="3" fill="#f5c4b8" opacity="0.5"/>
            <path d="M25 50 L32 54 L39 50" stroke="#0A2320" strokeWidth="2.5" fill="none"/>
          </svg>
        </div>
      </div>
      <div className="flex-1 relative">
        <div className="absolute left-0 top-3 w-0 h-0 border-t-6 border-t-transparent border-b-6 border-b-transparent border-r-6 border-r-white -ml-1.5"></div>
        <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
          <p className="text-[#153254] text-sm leading-relaxed">{message}</p>
        </div>
      </div>
    </div>
  );
};

// Service icons mapping
const serviceIcons = {
  menage: Home,
  jardinage: TreePine,
  bricolage: Wrench,
  demenagement: Truck,
  administratif: FileText,
  vehicule: Car,
  airbnb: Building
};

// Generate available time slots
const generateAvailableSlots = () => {
  const slots = [];
  const today = new Date();
  
  for (let i = 2; i < 30; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    
    const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const dayName = dayNames[date.getDay()];
    
    if (date.getDay() !== 0) {
      slots.push({
        date: date.toISOString().split('T')[0],
        dayName: `${dayName} ${date.getDate()}/${date.getMonth() + 1}`,
        times: ['08:00', '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00']
      });
    }
  }
  return slots;
};

// Intervenants
const intervenants = [
  { id: 1, name: 'Christie' },
  { id: 2, name: 'Franck' },
  { id: 3, name: 'Josué' }
];

const getPhotoUrl = (id) => {
  const photos = {
    1: 'https://customer-assets.emergentagent.com/job_a72f3521-3152-44ec-800d-97dc3c085d7c/artifacts/mlu2qlra_CHRISTIE%20%281%29.png',
    2: 'https://customer-assets.emergentagent.com/job_a72f3521-3152-44ec-800d-97dc3c085d7c/artifacts/avduy08n_FRANCK%20%281%29.png',
    3: 'https://customer-assets.emergentagent.com/job_a72f3521-3152-44ec-800d-97dc3c085d7c/artifacts/lm2erv75_JOSUE%20%281%29.png'
  };
  return photos[id];
};

// Guide messages for each step
const guideMessages = {
  1: "Bonjour ! 👋 Vous avez déjà reçu un devis et souhaitez prendre rendez-vous ? Parfait ! Sélectionnez d'abord le(s) service(s) concerné(s).",
  2: "Excellent choix ! Vous pouvez maintenant choisir votre intervenant préféré.",
  3: "Super ! Choisissez maintenant la date et l'heure qui vous conviennent le mieux.",
  4: "Dernière étape ! Renseignez vos coordonnées et joignez votre devis pour finaliser."
};

export const AppointmentBooking = () => {
  const [step, setStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedIntervenant, setSelectedIntervenant] = useState(null);
  const [appointment, setAppointment] = useState({ date: '', time: '', viewMonth: null, viewYear: null });
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    quoteFile: null,
    quoteFileName: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const availableSlots = useMemo(() => generateAvailableSlots(), []);

  // API backend URL
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Disponibilités agrégées (intersection des services sélectionnés)
  // Forme: { datesSet: Set<string>, slotsByDate: {[date]: string[]} }
  const [aggregatedAvailability, setAggregatedAvailability] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      if (!selectedServices || selectedServices.length === 0) {
        setAggregatedAvailability(null);
        return;
      }
      try {
        const results = await Promise.all(selectedServices.map(async (sid) => {
          const params = new URLSearchParams({ service_id: sid, days_to_check: '60' });
          const res = await fetch(`${API_URL}/api/availability?${params.toString()}`);
          if (!res.ok) return null;
          return res.json();
        }));
        if (cancelled) return;

        // Intersection des dates + intersection des slots par date
        let datesSet = null;
        let slotsByDate = {};
        let holidaysSet = new Set();
        results.forEach(r => {
          if (!r) return;
          const localSet = new Set();
          (r.available_dates || []).forEach(d => {
            localSet.add(d.date);
            if (slotsByDate[d.date] === undefined) {
              slotsByDate[d.date] = new Set(d.slots || []);
            } else {
              const prev = slotsByDate[d.date];
              slotsByDate[d.date] = new Set((d.slots || []).filter(s => prev.has(s)));
            }
            if (d.is_holiday) holidaysSet.add(d.date);
          });
          if (datesSet === null) {
            datesSet = localSet;
          } else {
            datesSet = new Set([...datesSet].filter(x => localSet.has(x)));
          }
        });
        // Convert slot Sets to sorted arrays, only keep dates with slots
        const finalSlots = {};
        const finalDates = new Set();
        (datesSet || new Set()).forEach(d => {
          const arr = Array.from(slotsByDate[d] || []).sort();
          if (arr.length > 0) {
            finalSlots[d] = arr;
            finalDates.add(d);
          }
        });
        setAggregatedAvailability({ datesSet: finalDates, slotsByDate: finalSlots, holidaysSet });
      } catch (e) {
        console.warn('availability fetch failed', e);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedServices)]);

  // Auto-jump calendar view to first available month
  useEffect(() => {
    if (!aggregatedAvailability || aggregatedAvailability.datesSet.size === 0) return;
    if (appointment.date || appointment.viewMonth !== undefined && appointment.viewMonth !== null) return;
    const firstDate = [...aggregatedAvailability.datesSet].sort()[0];
    if (!firstDate) return;
    const [y, m] = firstDate.split('-').map(n => parseInt(n, 10));
    setAppointment(prev => ({ ...prev, viewMonth: m - 1, viewYear: y }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregatedAvailability]);

  // Format date ISO -> "Jour N mois" FR
  const formatFrenchDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };
  
  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Le nom est requis';
        if (value.length < 2) return 'Le nom doit contenir au moins 2 caractères';
        return '';
      case 'email':
        if (!value.trim()) return 'L\'email est requis';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Veuillez entrer un email valide';
        return '';
      case 'phone':
        if (!value.trim()) return 'Le téléphone est requis';
        if (!/^(0[1-9])[\s.-]?([0-9]{2}[\s.-]?){4}$/.test(value.replace(/\s/g, ''))) {
          return 'Numéro invalide (ex: 06 12 34 56 78)';
        }
        return '';
      case 'address':
        if (!value.trim()) return 'L\'adresse est requise';
        if (value.length < 5) return 'Veuillez entrer une adresse complète';
        return '';
      default:
        return '';
    }
  };

  const handleContactChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleContactBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, contactInfo[field]) }));
  };

  const validateStep4 = () => {
    const newErrors = {};
    ['name', 'email', 'phone', 'address'].forEach(field => {
      const error = validateField(field, contactInfo[field]);
      if (error) newErrors[field] = error;
    });
    
    if (!contactInfo.quoteFile) {
      newErrors.quoteFile = 'Veuillez joindre votre devis';
    }
    
    setErrors(newErrors);
    setTouched({ name: true, email: true, phone: true, address: true, quoteFile: true });
    return Object.keys(newErrors).length === 0;
  };
  
  const goToStep = (newStep) => {
    setStep(newStep);
    window.scrollTo(0, 0);
  };
  
  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setContactInfo(prev => ({
        ...prev,
        quoteFile: file,
        quoteFileName: file.name
      }));
      setErrors(prev => ({ ...prev, quoteFile: '' }));
    }
  };
  
  const handleSubmit = async () => {
    if (!validateStep4()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('services', JSON.stringify(selectedServices));
      formData.append('intervenant_id', selectedIntervenant);
      formData.append('date', appointment.date);
      formData.append('time', appointment.time);
      formData.append('name', contactInfo.name);
      formData.append('email', contactInfo.email);
      formData.append('phone', contactInfo.phone);
      formData.append('address', contactInfo.address);
      if (contactInfo.quoteFile) {
        formData.append('quote_file', contactInfo.quoteFile);
      }
      
      const response = await fetch(`${API_URL}/api/rendez-vous`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Erreur lors de l\'envoi');
      }
      
      setSubmitted(true);
      toast.success('Rendez-vous confirmé ! Un email de confirmation vous a été envoyé.');
    } catch (error) {
      console.error('Error submitting appointment:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Calendar data
  const today = new Date();
  const currentMonth = appointment.viewMonth !== undefined && appointment.viewMonth !== null 
    ? appointment.viewMonth 
    : today.getMonth();
  const currentYear = appointment.viewYear !== undefined && appointment.viewYear !== null 
    ? appointment.viewYear 
    : today.getFullYear();
  const firstDay = new Date(currentYear, currentMonth, 1);
  const lastDay = new Date(currentYear, currentMonth + 1, 0);
  const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const daysInMonth = lastDay.getDate();
  
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                     'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  
  const isDateAvailable = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    const minDate = new Date(today.getTime() + 48 * 60 * 60 * 1000);
    if (date < minDate) return false;
    if (!aggregatedAvailability) return false;
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return aggregatedAvailability.datesSet.has(dateStr);
  };
  
  const formatDate = (day) => {
    return `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  
  // Error message component
  const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
      <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  };
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#153254]/5 via-[#F8FAF9] to-[#0A2320]/5">
        <Header />
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-lg mx-auto text-center bg-white rounded-2xl p-10 shadow-lg border border-[#153254]/20">
            <div className="w-20 h-20 bg-[#0A2320]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-[#0A2320]" />
            </div>
            <h2 className="text-2xl font-bold text-[#153254] mb-4">Rendez-vous confirmé !</h2>
            <p className="text-gray-600 mb-2">
              Votre rendez-vous a été enregistré avec succès.
            </p>
            <p className="text-[#0A2320] font-medium mb-6">
              📧 Un email de confirmation vous a été envoyé à {contactInfo.email}
            </p>
            <div className="bg-[#153254]/5 rounded-xl p-4 mb-6 text-left border border-[#153254]/20">
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold text-[#153254]">Date :</span> {formatFrenchDate(appointment.date)}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold text-[#153254]">Heure :</span> {appointment.time}
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold text-[#153254]">Services :</span> {selectedServices.map(id => services.find(s => s.id === id)?.title).join(', ')}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-semibold text-[#153254]">Intervenant :</span> {intervenants.find(i => i.id === selectedIntervenant)?.name}
              </p>
            </div>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-[#D4AF37] hover:bg-[#c9a030] text-white"
            >
              Retour à l'accueil
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#153254]/5 via-[#F8FAF9] to-[#0A2320]/5">
      <Header />
      
      {/* Header with blue accent - different from green quote tunnel */}
      <div className="bg-gradient-to-br from-[#1a4a6e] to-[#153254] py-8 md:py-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">Prise de rendez-vous</h1>
          <p className="text-white/90 text-sm md:text-base">Pour les personnes ayant déjà un devis.</p>
        </div>
      </div>
      
      {/* Info box for appointment booking */}
      <div className="container mx-auto px-4 mt-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#153254]/10 border border-[#153254]/30 rounded-xl p-4 mb-4">
            <p className="text-[#153254] text-sm md:text-base font-medium mb-2">
              Ce bouton vous permet de prendre rendez-vous pour un devis déjà validé.
            </p>
            <p className="text-[#153254]/80 text-sm">
              Pour une nouvelle demande de devis, utilisez le bouton "<a href="/devis" className="underline font-semibold hover:text-[#D4AF37]">Demander un devis</a>" sur le site.
            </p>
            <p className="text-amber-700 text-sm mt-2 font-medium">
              ⚠️ Si un rendez-vous est déjà fixé pour votre devis, merci de ne pas en reprendre un nouveau.
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 pb-20">
        <div className="max-w-4xl mx-auto min-h-[60vh]">
          {/* Progress Steps */}
          <div className="bg-white rounded-xl p-4 shadow-lg mb-6 border border-[#153254]/20">
            <div className="flex items-center justify-between">
              {[
                { num: 1, label: 'Services' },
                { num: 2, label: 'Intervenant' },
                { num: 3, label: 'Date & Heure' },
                { num: 4, label: 'Confirmation' }
              ].map((s, idx) => (
                <div key={s.num} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= s.num ? 'bg-[#153254] text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > s.num ? <Check className="h-4 w-4" /> : s.num}
                  </div>
                  <span className={`ml-2 text-sm hidden sm:inline ${step >= s.num ? 'text-[#153254] font-medium' : 'text-gray-400'}`}>
                    {s.label}
                  </span>
                  {idx < 3 && <div className={`w-8 md:w-16 h-0.5 mx-2 ${step > s.num ? 'bg-[#153254]' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          </div>
          
          {/* Main card */}
          <div className="bg-white rounded-xl p-6 shadow-lg border border-[#153254]/20">
            {/* Guide character */}
            <GuideCharacter message={guideMessages[step]} />
            
            {/* Step 1: Service Selection */}
            {step === 1 && (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#153254] mb-2">
                  De quel(s) service(s) avez-vous eu un devis ?
                </h2>
                <p className="text-gray-500 mb-6 text-sm">
                  Sélectionnez le(s) service(s) concerné(s) par votre devis
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                  {services.map(service => {
                    const IconComponent = serviceIcons[service.id] || Home;
                    const isSelected = selectedServices.includes(service.id);
                    return (
                      <button
                        key={service.id}
                        onClick={() => toggleService(service.id)}
                        data-testid={`service-${service.id}`}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected
                            ? 'border-[#153254] bg-[#153254]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <IconComponent className={`h-6 w-6 mb-2 ${isSelected ? 'text-[#153254]' : 'text-gray-400'}`} />
                        <span className={`font-medium text-sm ${isSelected ? 'text-[#153254]' : 'text-[#153254]'}`}>
                          {service.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
                
                {selectedServices.length === 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Veuillez sélectionner au moins un service pour continuer
                    </p>
                  </div>
                )}
                
                <div className="flex justify-end">
                  <Button 
                    onClick={() => goToStep(2)}
                    disabled={selectedServices.length === 0}
                    data-testid="continue-step1"
                    className="bg-[#D4AF37] hover:bg-[#c9a030] text-white px-6"
                  >
                    Continuer
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 2: Intervenant Selection */}
            {step === 2 && (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#153254] mb-2">
                  Choisissez votre intervenant
                </h2>
                <p className="text-gray-500 mb-6 text-sm">
                  Sélectionnez la personne qui interviendra chez vous
                </p>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {intervenants.map(intervenant => (
                    <button
                      key={intervenant.id}
                      onClick={() => setSelectedIntervenant(intervenant.id)}
                      data-testid={`intervenant-${intervenant.id}`}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        selectedIntervenant === intervenant.id
                          ? 'border-[#153254] bg-[#153254]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img 
                        src={getPhotoUrl(intervenant.id)} 
                        alt={intervenant.name}
                        className="w-20 h-20 mx-auto rounded-xl object-cover mb-2"
                      />
                      <span className="font-medium text-[#153254]">{intervenant.name}</span>
                      {selectedIntervenant === intervenant.id && (
                        <div className="mt-2">
                          <div className="inline-flex items-center gap-1 bg-[#153254] text-white text-xs px-2 py-1 rounded-full">
                            <Check className="h-3 w-3" />
                            Sélectionné
                          </div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                
                {!selectedIntervenant && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Veuillez choisir un intervenant pour continuer
                    </p>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <Button 
                    variant="outline"
                    onClick={() => goToStep(1)}
                    className="border-[#153254] text-[#153254]"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Retour
                  </Button>
                  <Button 
                    onClick={() => goToStep(3)}
                    disabled={!selectedIntervenant}
                    data-testid="continue-step2"
                    className="bg-[#D4AF37] hover:bg-[#c9a030] text-white px-6"
                  >
                    Continuer
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 3: Date & Time Selection */}
            {step === 3 && (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#153254] mb-2">
                  Choisissez votre créneau
                </h2>
                <p className="text-gray-500 mb-4 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Créneaux disponibles à partir de 48h
                </p>
                
                {/* Calendar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <button 
                      onClick={() => setAppointment(prev => ({
                        ...prev,
                        viewMonth: currentMonth === 0 ? 11 : currentMonth - 1,
                        viewYear: currentMonth === 0 ? currentYear - 1 : currentYear
                      }))}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <ArrowLeft className="h-5 w-5 text-gray-600" />
                    </button>
                    <span className="font-semibold text-[#153254]">
                      {monthNames[currentMonth]} {currentYear}
                    </span>
                    <button 
                      onClick={() => setAppointment(prev => ({
                        ...prev,
                        viewMonth: currentMonth === 11 ? 0 : currentMonth + 1,
                        viewYear: currentMonth === 11 ? currentYear + 1 : currentYear
                      }))}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <ArrowRight className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'].map((day, idx) => (
                      <div key={idx} className="text-center text-sm font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1">
                    {[...Array(startPadding)].map((_, i) => (
                      <div key={`empty-${i}`} className="h-10" />
                    ))}
                    
                    {[...Array(daysInMonth)].map((_, i) => {
                      const day = i + 1;
                      const dateStr = formatDate(day);
                      const isAvailable = isDateAvailable(day);
                      const isSelected = appointment.date === dateStr;
                      const isHoliday = aggregatedAvailability?.holidaysSet?.has(dateStr);
                      
                      return (
                        <button
                          key={day}
                          onClick={() => isAvailable && setAppointment(prev => ({ ...prev, date: dateStr, time: '' }))}
                          disabled={!isAvailable}
                          title={isHoliday ? 'Jour férié — ouvert' : undefined}
                          className={`relative h-10 rounded-lg text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-[#153254] text-white'
                              : isAvailable
                                ? isHoliday
                                  ? 'bg-amber-50 hover:bg-amber-100 text-amber-900 ring-1 ring-amber-300'
                                  : 'bg-gray-50 hover:bg-[#153254]/10 text-[#153254]'
                                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                          }`}
                        >
                          {day}
                          {isHoliday && isAvailable && (
                            <span className="absolute -top-1 -right-1 text-[9px] leading-none">🇫🇷</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Time Slots */}
                {appointment.date && (
                  <div className="mb-6">
                    <p className="font-medium text-[#153254] mb-3">Choisir une heure</p>
                    <div className="grid grid-cols-4 gap-2">
                      {(aggregatedAvailability?.slotsByDate?.[appointment.date] || []).map((time) => (
                        <button
                          key={time}
                          onClick={() => setAppointment(prev => ({ ...prev, time }))}
                          className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                            appointment.time === time
                              ? 'bg-[#153254] text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-[#153254]'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {appointment.date && appointment.time && (
                  <div className="mb-6 bg-[#153254]/5 rounded-xl p-4 border border-[#153254]/20">
                    <p className="text-[#153254] font-medium flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      {formatFrenchDate(appointment.date)} à {appointment.time}
                    </p>
                  </div>
                )}
                
                {(!appointment.date || !appointment.time) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {!appointment.date ? 'Veuillez sélectionner une date' : 'Veuillez sélectionner une heure'}
                    </p>
                  </div>
                )}
                
                {/* Urgent mention */}
                <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>Si cela est urgent, n'hésitez pas à nous appeler au <a href="tel:0981543842" className="font-bold underline">09 81 54 38 42</a></span>
                  </p>
                </div>
                
                <div className="flex justify-between">
                  <Button 
                    variant="outline"
                    onClick={() => goToStep(2)}
                    className="border-[#153254] text-[#153254]"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Retour
                  </Button>
                  <Button 
                    onClick={() => goToStep(4)}
                    disabled={!appointment.date || !appointment.time}
                    data-testid="continue-step3"
                    className="bg-[#D4AF37] hover:bg-[#c9a030] text-white px-6"
                  >
                    Continuer
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 4: Confirmation with Quote Upload */}
            {step === 4 && (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-[#153254] mb-2">
                  Confirmez votre rendez-vous
                </h2>
                <p className="text-gray-500 mb-6 text-sm">
                  Joignez votre devis pour finaliser la prise de rendez-vous
                </p>
                
                {/* Appointment Summary */}
                <div className="bg-[#153254]/5 rounded-xl p-5 mb-6 border border-[#153254]/20">
                  <h3 className="font-bold text-[#153254] mb-3">Récapitulatif du rendez-vous</h3>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#153254]" />
                      <span className="text-gray-600">Date :</span>
                      <span className="font-medium text-[#153254]">
                        {formatFrenchDate(appointment.date)}
                      </span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#153254]" />
                      <span className="text-gray-600">Heure :</span>
                      <span className="font-medium text-[#153254]">{appointment.time}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-[#153254]" />
                      <span className="text-gray-600">Services :</span>
                      <span className="font-medium text-[#153254]">
                        {selectedServices.map(id => services.find(s => s.id === id)?.title).join(', ')}
                      </span>
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-[#153254]" />
                      <span className="text-gray-600">Intervenant :</span>
                      <span className="font-medium text-[#153254]">
                        {intervenants.find(i => i.id === selectedIntervenant)?.name}
                      </span>
                    </p>
                  </div>
                </div>
                
                {/* Contact Info */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-[#153254] mb-1">Nom complet *</label>
                    <Input 
                      value={contactInfo.name}
                      onChange={(e) => handleContactChange('name', e.target.value)}
                      onBlur={() => handleContactBlur('name')}
                      placeholder="Jean Dupont"
                      data-testid="input-name"
                      className={`border-gray-300 ${errors.name && touched.name ? 'border-red-400 focus:ring-red-400' : ''}`}
                    />
                    <ErrorMessage error={touched.name && errors.name} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#153254] mb-1">Email *</label>
                      <Input 
                        type="email"
                        value={contactInfo.email}
                        onChange={(e) => handleContactChange('email', e.target.value)}
                        onBlur={() => handleContactBlur('email')}
                        placeholder="jean@email.com"
                        data-testid="input-email"
                        className={`border-gray-300 ${errors.email && touched.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                      />
                      <ErrorMessage error={touched.email && errors.email} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#153254] mb-1">Téléphone *</label>
                      <Input 
                        type="tel"
                        value={contactInfo.phone}
                        onChange={(e) => handleContactChange('phone', e.target.value)}
                        onBlur={() => handleContactBlur('phone')}
                        placeholder="06 12 34 56 78"
                        data-testid="input-phone"
                        className={`border-gray-300 ${errors.phone && touched.phone ? 'border-red-400 focus:ring-red-400' : ''}`}
                      />
                      <ErrorMessage error={touched.phone && errors.phone} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#153254] mb-1">Adresse d'intervention *</label>
                    <Input 
                      value={contactInfo.address}
                      onChange={(e) => handleContactChange('address', e.target.value)}
                      onBlur={() => handleContactBlur('address')}
                      placeholder="123 Rue de la Paix, 60110 Méru"
                      data-testid="input-address"
                      className={`border-gray-300 ${errors.address && touched.address ? 'border-red-400 focus:ring-red-400' : ''}`}
                    />
                    <ErrorMessage error={touched.address && errors.address} />
                  </div>
                  
                  {/* Quote Upload */}
                  <div>
                    <label className="block text-sm font-medium text-[#153254] mb-2">
                      Joindre votre devis (PDF, image) *
                    </label>
                    <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                      contactInfo.quoteFile 
                        ? 'border-green-400 bg-green-50' 
                        : errors.quoteFile && touched.quoteFile
                          ? 'border-red-400 bg-red-50'
                          : 'border-gray-300 hover:border-[#153254]'
                    }`}>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        className="hidden"
                        id="quote-upload"
                        data-testid="input-quote-file"
                      />
                      <label htmlFor="quote-upload" className="cursor-pointer">
                        {contactInfo.quoteFile ? (
                          <div className="flex items-center justify-center gap-3">
                            <FileUp className="h-8 w-8 text-green-600" />
                            <div className="text-left">
                              <p className="font-medium text-green-700">{contactInfo.quoteFileName}</p>
                              <p className="text-sm text-green-600">Fichier prêt à être envoyé</p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <Upload className={`h-10 w-10 mx-auto mb-2 ${errors.quoteFile && touched.quoteFile ? 'text-red-400' : 'text-gray-400'}`} />
                            <p className={`font-medium ${errors.quoteFile && touched.quoteFile ? 'text-red-600' : 'text-gray-600'}`}>
                              Cliquez pour télécharger votre devis
                            </p>
                            <p className="text-sm text-gray-400 mt-1">PDF, JPG, PNG (max 10MB)</p>
                          </>
                        )}
                      </label>
                    </div>
                    <ErrorMessage error={touched.quoteFile && errors.quoteFile} />
                  </div>
                </div>
                
                <div className="flex justify-between">
                  <Button 
                    variant="outline"
                    onClick={() => goToStep(3)}
                    className="border-[#153254] text-[#153254]"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Retour
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    data-testid="submit-appointment"
                    className="bg-[#D4AF37] hover:bg-[#c9a030] text-white px-8"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Confirmer le rendez-vous
                        <CheckCircle className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};
